import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './StudyTimer.css';
import { useAuth } from '../../context/AuthContext';
import { useCoins } from '../../context/CoinContext';
import { engine } from '../../core/books/storage.js';
import { getToken } from '../../utils/secureStorage.js';
import {
  GRADE_LIST, FIELD_LIST,
  needsField as curriculumNeedsField,
  normalizeFieldId as curriculumNormalizeField,
  normalizeGradeId as curriculumNormalizeGrade,
  getSubjectsFor, getSubjectTitle,
} from '../../core/education/curriculum.js';

/* ============================================================
   HELPERS
============================================================ */
const PD = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
const fa = (n) => String(n).replace(/[0-9]/g, (d) => PD[+d]);
const pad = (n) => String(n).padStart(2, '0');
const formatTime = (s) => {
  s = Math.max(0, Math.floor(s));
  return pad(Math.floor(s / 60)) + ':' + pad(s % 60);
};

const PRESETS = {
  pomo:     { name: 'پومودورو', work: 25, break: 5,  rounds: 4 },
  deep:     { name: 'عمیق',    work: 50, break: 10, rounds: 3 },
  flow:     { name: 'فلوتایم', work: 60, break: 0,  rounds: 0 },
  marathon: { name: 'ماراتن',  work: 60, break: 5,  rounds: 5 },
  kid:      { name: 'کودک',    work: 15, break: 5,  rounds: 4 },
};

const WEEK_DAYS = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];
const GENERIC_LESSONS = ['درس ۱','درس ۲','درس ۳','درس ۴','درس ۵','درس ۶','درس ۷','درس ۸','درس ۹','درس ۱۰'];

const DAY_MAP = { Saturday: 0, Sunday: 1, Monday: 2, Tuesday: 3, Wednesday: 4, Thursday: 5, Friday: 6 };
const localYMD = (d = new Date()) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayKey = () => localYMD(new Date());
const todayIndex = () => DAY_MAP[new Date().toLocaleDateString('en-US', { weekday: 'long' })] ?? 0;

/* ---------- ADAPTIVE GOAL ---------- */
const GOAL_MIN = 15;
const GOAL_MAX = 360;
const GOAL_MAX_BUMP = 30;
const GOAL_RETURN_CAP = 30;
const GOAL_DECAY = 0.85;

const daysBetween = (a, b) => {
  if (!a || !b) return 0;
  const msA = new Date(a + 'T00:00:00').getTime();
  const msB = new Date(b + 'T00:00:00').getTime();
  return Math.round((msB - msA) / 86400000);
};

const clampGoal = (v) => Math.max(GOAL_MIN, Math.min(GOAL_MAX, Math.round(v)));

const formatHM = (mins) => {
  const m = Math.max(0, Math.floor(mins));
  return Math.floor(m / 60) + ':' + pad(m % 60);
};

const computeTodayGoal = (ag, today, dailyProgress) => {
  if (!ag) return null;
  if (ag.goalDate === today) return ag.todayGoal ?? null;
  if (!ag.lastActiveDate) return null;

  const lastActual = (dailyProgress && dailyProgress[ag.lastActiveDate]) || 0;
  const lastGoal = ag.lastActiveGoal || lastActual || GOAL_MIN;
  const gap = daysBetween(ag.lastActiveDate, today);

  if (gap <= 1) {
    let t;
    if (lastActual >= lastGoal) t = lastActual + Math.max(5, Math.round(lastActual * 0.1));
    else if (lastActual >= lastGoal * 0.5) t = lastActual + 3;
    else if (lastActual > 0) t = Math.round(lastActual * 0.9);
    else t = lastGoal;
    t = Math.min(t, lastGoal + GOAL_MAX_BUMP);
    return clampGoal(t);
  }

  const decayed = lastGoal * Math.pow(GOAL_DECAY, gap - 1);
  const floor = Math.max(GOAL_MIN, Math.min((ag.record || 0) * 0.35, GOAL_RETURN_CAP));
  return clampGoal(Math.max(decayed, floor));
};

/* ============================================================
   CURRICULUM TREE
============================================================ */
const buildCurriculumTree = () => {
  const tree = {};
  GRADE_LIST.forEach((g) => {
    if (g.hasFields) {
      const fields = {};
      FIELD_LIST.forEach((f) => {
        const books = {};
        getSubjectsFor(g.id, f.id).forEach((s) => {
          books[getSubjectTitle(g.id, s.id)] = { chapters: [...GENERIC_LESSONS] };
        });
        fields[f.title] = { books };
      });
      tree[g.title] = { fields };
    } else {
      const books = {};
      getSubjectsFor(g.id).forEach((s) => {
        books[getSubjectTitle(g.id, s.id)] = { chapters: [...GENERIC_LESSONS] };
      });
      tree[g.title] = { books };
    }
  });
  return tree;
};






/* ============================================================
   LEADERBOARD HELPERS — منبع واحد محاسبه دقیقه مطالعه
============================================================ */
const computeStudyMinutesForPeriod = (u, filter, now = new Date()) => {
  if (!u) return 0;
  const dp = (u.dailyProgress && typeof u.dailyProgress === 'object') ? u.dailyProgress : {};

  if (filter === 'today') {
    return Number(dp[localYMD(now)]) || 0;
  }

  if (filter === 'week') {
    const idx = todayIndex(); // 0=شنبه ... 6=جمعه
    let sum = 0;
    for (let i = 0; i <= idx; i++) {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
      sum += Number(dp[localYMD(d)]) || 0;
    }
    return sum;
  }

  if (filter === 'month') {
    const mp = (u.monthlyProgress && typeof u.monthlyProgress === 'object') ? u.monthlyProgress : {};
    const mk = `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
    if (mp[mk] != null) return Number(mp[mk]) || 0;
    // fallback: جمع روزهای همان ماه از dailyProgress
    let sum = 0;
    const y = now.getFullYear(), m = now.getMonth();
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    for (let d = 1; d <= daysInMonth; d++) {
      sum += Number(dp[localYMD(new Date(y, m, d))]) || 0;
    }
    return sum;
  }

  return 0;
};









/* ============================================================
   useAllUsers — گرفتن لیست کامل کاربران از سرور
============================================================ */
function useAllUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

const refresh = useCallback(async (opts = {}) => {
  const silent = opts.silent === true;
  try {
    if (abortRef.current) { try { abortRef.current.abort(); } catch {} }
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    if (!silent) { setLoading(true); setError(null); }

    const token = getToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const res = await fetch('/api/db/users', {
      headers,
      signal: ctrl.signal,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    setUsers(Array.isArray(data) ? data : []);
    if (!silent) { setError(null); setLoading(false); }
  } catch (e) {
    if (e && e.name === 'AbortError') return;
    if (!silent) { setError({ type: 'network' }); setLoading(false); }
  }
}, []);

  useEffect(() => {
    refresh();
    return () => {
      if (abortRef.current) { try { abortRef.current.abort(); } catch {} }
    };
  }, [refresh]);

  return { users, loading, error, refresh };
}







/* ============================================================
   CONFETTI
============================================================ */
const Confetti = ({ count = 90, duration = 3500 }) => {
  const [pieces, setPieces] = useState([]);
  useEffect(() => {
    const colors = ['#7C74F0', '#A78BFA', '#C4B5FD', '#60A5FA', '#F472B6', '#2DD4BF', '#FBBF24'];
    const list = Array.from({ length: count }, () => ({
      id: Math.random().toString(36).slice(2),
      left: Math.random() * 100,
      color: colors[Math.floor(Math.random() * colors.length)],
      size: 6 + Math.random() * 8,
      delay: Math.random() * 0.5,
      dur: 2 + Math.random() * 1.5,
    }));
    setPieces(list);
    const t = setTimeout(() => setPieces([]), duration);
    return () => clearTimeout(t);
  }, [count, duration]);
  if (!pieces.length) return null;
  return (
    <div className="st-confetti-layer">
      {pieces.map((p) => (
        <div
          key={p.id}
          className="st-confetti-piece"
          style={{
            left: `${p.left}%`,
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
            boxShadow: `0 0 10px ${p.color}`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.dur}s`,
          }}
        />
      ))}
    </div>
  );
};

/* ============================================================
   RECORD TOAST
============================================================ */
const RecordToast = ({ value }) => {
  const [show, setShow] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setShow(false), 3200);
    return () => clearTimeout(t);
  }, []);
  if (!show) return null;
  return (
    <div className="st-record-toast">
      <div className="st-record-toast-icon">🏆</div>
      <div className="st-record-toast-text">رکورد جدید: {fa(formatHM(value || 0))}</div>
    </div>
  );
};








/* ============================================================
   STUDY LEADERBOARD — جدول برترین‌های مطالعه
============================================================ */
const StudyLeaderboard = ({ currentUserId, currentUserIsGuest, refreshTrigger }) => {
  const { users, loading, error, refresh } = useAllUsers();
  const [timeFilter, setTimeFilter] = useState('today');

  // رفرش خودکار هر ۶۰ ثانیه — بی‌صدا
useEffect(() => {
  if (loading) return;
  const id = setInterval(() => { refresh({ silent: true }); }, 60000);
  return () => clearInterval(id);
}, [loading, refresh]);

  // رفرش بعد از اتمام جلسه مطالعه (از والد)
  useEffect(() => {
    if (!refreshTrigger) return;
    refresh({ silent: true });
  }, [refreshTrigger, refresh]);

  const { list, myRank } = useMemo(() => {
    if (!Array.isArray(users) || users.length === 0) {
      return { list: [], myRank: 0 };
    }
    const now = new Date();
    const rows = [];
    for (const u of users) {
      if (!u || !u.id) continue;
      const idStr = String(u.id);
      if (idStr.startsWith('guest_')) continue;
      const minutes = computeStudyMinutesForPeriod(u, timeFilter, now);
      if (minutes <= 0) continue;
      const name = String(u.fullName || u.name || 'کاربر').trim() || 'کاربر';
      rows.push({ id: idStr, name, minutes, isMe: idStr === String(currentUserId) });
    }
    rows.sort((a, b) => b.minutes - a.minutes || a.name.localeCompare(b.name, 'fa'));
    const myRank = rows.findIndex(r => r.isMe) + 1;
    return { list: rows, myRank };
  }, [users, timeFilter, currentUserId]);

  const top3 = list.slice(0, 3);
  const rest = list.slice(3, 14);
  const me = list.find(r => r.isMe);

  return (
    <section className="st-leaderboard">
      <div className="st-lb-card">
        <div className="st-lb-header">
          <div className="st-lb-title">🏆 جدول برترین‌های مطالعه</div>
          <div className="st-lb-subtitle">رتبه‌بندی بر اساس زمان مطالعه — کل کشور</div>
        </div>

        <div className="st-lb-filters">
          {[
            { k: 'today', label: 'روزانه' },
            { k: 'week',  label: 'هفتگی' },
            { k: 'month', label: 'ماهانه' },
          ].map(o => (
            <button
              key={o.k}
              type="button"
              className={`st-lb-filter-btn ${timeFilter === o.k ? 'st-active' : ''}`}
              onClick={() => setTimeFilter(o.k)}
            >
              {o.label}
            </button>
          ))}
        </div>

        {loading && (
          <div className="st-lb-loading">
            <div className="st-lb-spinner" />
            <p>در حال بارگذاری...</p>
          </div>
        )}

{!loading && error && (
  <div className="st-lb-error">
    <p>خطا در بارگذاری جدول</p>
    <button type="button" onClick={() => refresh()}>تلاش مجدد</button>
  </div>
)}

        {!loading && !error && list.length === 0 && (
          <div className="st-lb-empty">
            <p>هنوز کسی در این بازه مطالعه‌ای ثبت نکرده</p>
          </div>
        )}

        {!loading && !error && list.length > 0 && (
          <>
            <div className="st-lb-podium">
              {[1, 0, 2].map(i => {
                const u = top3[i];
                if (!u) return null;
                const place = i + 1;
                const medal = place === 1 ? '🥇' : place === 2 ? '🥈' : '🥉';
                return (
                  <div
                    key={u.id}
                    className={`st-lb-podium-item st-lb-p${place} ${u.isMe ? 'is-me' : ''}`}
                  >
                    <div className="st-lb-medal">{medal}</div>
                    <div className="st-lb-name" title={u.name}>{u.name}</div>
                    <div className="st-lb-score">{fa(formatHM(u.minutes))}</div>
                    <div className={`st-lb-base st-lb-base-p${place}`} />
                  </div>
                );
              })}
            </div>

            {rest.length > 0 && (
              <div className="st-lb-ranklist">
                {rest.map((u, idx) => {
                  const rank = idx + 4;
                  return (
                    <div key={u.id} className={`st-lb-rankrow ${u.isMe ? 'is-me' : ''}`}>
                      <span className="st-lb-ranknum">{fa(rank)}</span>
                      <span className="st-lb-rankname" title={u.name}>{u.name}</span>
                      <span className="st-lb-rankscore">{fa(formatHM(u.minutes))}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {myRank === 0 && (
              <div className="st-lb-myrrank">
                <div className="st-lb-myrrank-label">رتبه‌ی شما</div>
                <div className="st-lb-myrrank-msg">
                  {currentUserIsGuest
                    ? 'برای ورود به جدول، وارد حساب کاربری خود شوید'
                    : 'با اولین جلسه‌ی مطالعه در این بازه وارد جدول می‌شوی'}
                </div>
              </div>
            )}

            {myRank > 14 && me && (
              <div className="st-lb-myrrank">
                <div className="st-lb-myrrank-label">رتبه‌ی شما</div>
                <div className="st-lb-rankrow is-me">
                  <span className="st-lb-ranknum">{fa(myRank)}</span>
                  <span className="st-lb-rankname" title={me.name}>{me.name}</span>
                  <span className="st-lb-rankscore">{fa(formatHM(me.minutes))}</span>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};







/* ============================================================
   MAIN COMPONENT
============================================================ */
const StudyTimer = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { addRewards } = useCoins();
  const curriculumData = useMemo(() => buildCurriculumTree(), []);

  /* ---------- stable user id ---------- */
  const userId = useMemo(() => {
    if (user?.id) return user.id;
    let g = sessionStorage.getItem('studyTimerGuestId');
    if (!g) {
      g = `guest_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem('studyTimerGuestId', g);
    }
    return g;
  }, [user]);

  /* ---------- timer ---------- */
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [phase, setPhase] = useState('study'); // 'study' | 'break'
  const [totalSeconds, setTotalSeconds] = useState(25 * 60);
  const [remainingSeconds, setRemainingSeconds] = useState(25 * 60);
  const [breakTotal, setBreakTotal] = useState(0);
  const [breakRemaining, setBreakRemaining] = useState(0);
  const [currentRound, setCurrentRound] = useState(1);

  /* ---------- mode ---------- */
  const [modeKey, setModeKey] = useState('pomo');
  const [modeName, setModeName] = useState('پومودورو');
  const [workMinutes, setWorkMinutes] = useState(25);
  const [breakMinutes, setBreakMinutes] = useState(5);
  const [rounds, setRounds] = useState(4);
  const [customModes, setCustomModes] = useState(() =>
    Array.isArray(user?.customModes) ? user.customModes : []
  );

  /* ---------- session (book / chapter) ---------- */
  const userGrade = user?.grade || user?.class || '';
  const [selectedGrade, setSelectedGrade] = useState(() => {
    if (!userGrade) return '';
    const id = curriculumNormalizeGrade(userGrade);
    if (!id) return '';
    return GRADE_LIST.find((x) => x.id === id)?.title || '';
  });
  const [selectedField, setSelectedField] = useState(() => {
    if (!user?.field) return '';
    const id = curriculumNormalizeField(user.field);
    if (!id) return '';
    return FIELD_LIST.find((x) => x.id === id)?.title || '';
  });
  const [selectedBook, setSelectedBook] = useState('');
  const [selectedChapter, setSelectedChapter] = useState('');
  const [availableBooks, setAvailableBooks] = useState([]);
  const [availableChapters, setAvailableChapters] = useState([]);
  const [availableFields, setAvailableFields] = useState([]);

  /* ---------- stats ---------- */
  const [dailyProgress, setDailyProgress] = useState(() =>
    (user?.dailyProgress && typeof user.dailyProgress === 'object') ? user.dailyProgress : {}
  );
  const [weeklyProgress, setWeeklyProgress] = useState(() =>
    Array.isArray(user?.weeklyProgress) ? user.weeklyProgress : [0, 0, 0, 0, 0, 0, 0]
  );
  const [monthlyProgress, setMonthlyProgress] = useState(() =>
    (user?.monthlyProgress && typeof user.monthlyProgress === 'object') ? user.monthlyProgress : {}
  );
  const [bookStats, setBookStats] = useState(() =>
    (user?.bookStats && typeof user.bookStats === 'object') ? user.bookStats : {}
  );
  const [bookDailyStats, setBookDailyStats] = useState(() =>
    (user?.bookDailyStats && typeof user.bookDailyStats === 'object') ? user.bookDailyStats : {}
  );
  const [streak, setStreak] = useState(() => {
    try {
      const data = engine.get(`streak-${userId}`, null);
if (data && typeof data === 'object') {
  const today = localYMD(new Date());
  const yest = localYMD(new Date(Date.now() - 86400000));
        if (data.lastActivityDate === today || data.lastActivityDate === yest) {
          return Number(data.streak) || 0;
        }
      }
    } catch {}
    return 0;
  });

  /* ---------- modals ---------- */
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isSessionOpen, setIsSessionOpen] = useState(false);
  const [isAddModeOpen, setIsAddModeOpen] = useState(false);
  const [formWork, setFormWork] = useState(25);
  const [formBreak, setFormBreak] = useState(5);
  const [formRounds, setFormRounds] = useState(4);
  const [newModeName, setNewModeName] = useState('');
const [confettiKey, setConfettiKey] = useState(0);
const [recordToastKey, setRecordToastKey] = useState(0);
const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [adaptiveGoal, setAdaptiveGoal] = useState(() => {
    if (user?.adaptiveGoal && typeof user.adaptiveGoal === 'object') return user.adaptiveGoal;
    const dp = user?.dailyProgress || {};
    const days = Object.keys(dp).filter((d) => dp[d] > 0).sort();
    if (!days.length) return {};
    const lastDate = days[days.length - 1];
    const lastActual = dp[lastDate];
    const rec = Math.max(...days.map((d) => dp[d]));
    const recDate = days.find((d) => dp[d] === rec) || lastDate;
    return {
      goalDate: null,
      todayGoal: null,
      lastActiveDate: lastDate,
      lastActiveGoal: lastActual,
      record: rec,
      recordDate: recDate,
    };
  });

  /* ---------- refs ---------- */
  const stateRef = useRef({});
  stateRef.current = { isRunning, isPaused, phase };
  const completionRef = useRef(null);
  const initialUserIdRef = useRef(userId);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  /* ============================================================
     EFFECTS
  ============================================================ */
  useEffect(() => {
    if (!user?.id) return;
    updateUser({
      customModes,
      dailyProgress,
      weeklyProgress,
      monthlyProgress,
      bookStats,
      bookDailyStats,
      adaptiveGoal,
    });
  }, [customModes, dailyProgress, weeklyProgress, monthlyProgress, bookStats, bookDailyStats, adaptiveGoal]); // eslint-disable-line

  useEffect(() => {
    if (initialUserIdRef.current === userId) return;
    initialUserIdRef.current = userId;
    if (!user?.id) return;
    setCustomModes(Array.isArray(user.customModes) ? user.customModes : []);
    setDailyProgress((user.dailyProgress && typeof user.dailyProgress === 'object') ? user.dailyProgress : {});
    setWeeklyProgress(Array.isArray(user.weeklyProgress) ? user.weeklyProgress : [0, 0, 0, 0, 0, 0, 0]);
    setMonthlyProgress((user.monthlyProgress && typeof user.monthlyProgress === 'object') ? user.monthlyProgress : {});
    setBookStats((user.bookStats && typeof user.bookStats === 'object') ? user.bookStats : {});
    setBookDailyStats((user.bookDailyStats && typeof user.bookDailyStats === 'object') ? user.bookDailyStats : {});
    setAdaptiveGoal((user.adaptiveGoal && typeof user.adaptiveGoal === 'object') ? user.adaptiveGoal : {});
  }, [userId, user?.id]); // eslint-disable-line

  useEffect(() => {
    const gradeData = selectedGrade ? curriculumData[selectedGrade] : null;
    if (gradeData?.fields) {
      setAvailableFields(Object.keys(gradeData.fields));
      if (selectedField && gradeData.fields[selectedField]) {
        const books = Object.keys(gradeData.fields[selectedField].books);
        setAvailableBooks(books);
        if (!books.includes(selectedBook)) { setSelectedBook(''); setSelectedChapter(''); }
      } else {
        setAvailableBooks([]);
      }
    } else if (gradeData?.books) {
      setAvailableFields([]);
      const books = Object.keys(gradeData.books);
      setAvailableBooks(books);
      if (!books.includes(selectedBook)) { setSelectedBook(''); setSelectedChapter(''); }
    } else {
      setAvailableFields([]);
      setAvailableBooks([]);
    }
  }, [selectedGrade, selectedField]); // eslint-disable-line

  useEffect(() => {
    const gradeData = selectedGrade ? curriculumData[selectedGrade] : null;
    let bookData = null;
    if (gradeData?.fields && selectedField && gradeData.fields[selectedField]) {
      bookData = gradeData.fields[selectedField].books[selectedBook];
    } else if (gradeData?.books) {
      bookData = gradeData.books[selectedBook];
    }
    if (bookData) {
      setAvailableChapters(bookData.chapters || []);
      if (!bookData.chapters?.includes(selectedChapter)) setSelectedChapter('');
    } else {
      setAvailableChapters([]);
    }
  }, [selectedGrade, selectedField, selectedBook]); // eslint-disable-line

  useEffect(() => {
    if (!userId || userId === 'unknown') return;
    try {
      if (isRunning && !isPaused) {
        engine.set(`study_active_${userId}`, {
          book: selectedBook || 'مطالعه',
          topic: selectedChapter || '',
          startedAt: Date.now(),
        });
      } else {
        engine.remove(`study_active_${userId}`);
      }
    } catch {}
  }, [isRunning, isPaused, userId, selectedBook, selectedChapter]);

  useEffect(() => {
    return () => {
      try { engine.remove(`study_active_${userId}`); } catch {}
    };
  }, [userId]);

  useEffect(() => {
    const t = todayKey();
    if (adaptiveGoal.goalDate === t) return;
    const goal = computeTodayGoal(adaptiveGoal, t, dailyProgress);
    setAdaptiveGoal((ag) => ({ ...ag, goalDate: t, todayGoal: goal }));
  }, [dailyProgress, adaptiveGoal.goalDate]); // eslint-disable-line

  /* ============================================================
     COMPLETION / ADVANCEMENT
  ============================================================ */
const saveSessionStats = useCallback((mins, bookOverride) => {
  if (!mins || mins <= 0) return;
  const now = new Date();
  const dateKey = localYMD(now);
    const weekIdx = todayIndex();
    const monthKey = `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
    const bookName = bookOverride || selectedBook || 'عمومی';

    const prevToday = dailyProgress[dateKey] || 0;
    const nextToday = prevToday + mins;
    const prevRecord = adaptiveGoal.record || 0;
    const willBeRecord = nextToday > prevRecord;

    setDailyProgress((p) => ({ ...p, [dateKey]: (p[dateKey] || 0) + mins }));
    setWeeklyProgress((p) => {
      const arr = [...p];
      arr[weekIdx] = (arr[weekIdx] || 0) + mins;
      return arr;
    });
    setMonthlyProgress((p) => ({ ...p, [monthKey]: (p[monthKey] || 0) + mins }));
    setBookStats((p) => ({ ...p, [bookName]: (p[bookName] || 0) + mins }));
    setBookDailyStats((p) => {
      const next = { ...p };
      if (!next[bookName]) next[bookName] = {};
      next[bookName][dateKey] = (next[bookName][dateKey] || 0) + mins;
      return next;
    });

    setAdaptiveGoal((ag) => ({
      ...ag,
      lastActiveDate: dateKey,
      lastActiveGoal: ag.todayGoal ?? ag.lastActiveGoal ?? null,
      record: willBeRecord ? nextToday : (ag.record || 0),
      recordDate: willBeRecord ? dateKey : (ag.recordDate || null),
    }));

    if (willBeRecord) {
      setRecordToastKey((k) => k + 1);
      setConfettiKey((k) => k + 1);
    }

    setTimeout(() => setRefreshTrigger((k) => k + 1), 1200);
  }, [dailyProgress, adaptiveGoal, selectedBook]);

const updateStreak = useCallback(() => {
  const today = localYMD(new Date());
  const key = `streak-${userId}`;
  const saved = engine.get(key, null);
  let data = saved && typeof saved === 'object' ? saved : { streak: 0, lastActivityDate: null };
  if (data.lastActivityDate === today) return;
  const yest = localYMD(new Date(Date.now() - 86400000));
  data.streak = data.lastActivityDate === yest ? (data.streak || 0) + 1 : 1;
    data.lastActivityDate = today;
    engine.set(key, data);
    setStreak(data.streak);
  }, [userId]);

  const playAlarm = useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 880;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.25, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 3);
      osc.start();
      osc.stop(ctx.currentTime + 3);
      setTimeout(() => ctx.close(), 3100);
    } catch {}
  }, []);

  const advanceFromStudy = useCallback((saveStats) => {
    if (saveStats) {
      playAlarm();
      saveSessionStats(workMinutes, selectedBook);
      addRewards(5, 10);
      updateStreak();
    }

    const allDone = rounds > 0 && currentRound >= rounds;
    if (allDone) {
      setConfettiKey((k) => k + 1);
      setCurrentRound(1);
      setPhase('study');
      setTotalSeconds(workMinutes * 60);
      setRemainingSeconds(workMinutes * 60);
      setIsRunning(false);
      setIsPaused(false);
      return;
    }

    if (breakMinutes > 0) {
      setPhase('break');
      setBreakTotal(breakMinutes * 60);
      setBreakRemaining(breakMinutes * 60);
      setIsRunning(false);
      setIsPaused(false);
      // Auto-start break so it actually passes
      setTimeout(() => {
        if (phaseRef.current === 'break') {
          setIsRunning(true);
          setIsPaused(false);
        }
      }, 400);
      return;
    }

    setCurrentRound(currentRound + 1);
    setPhase('study');
    setTotalSeconds(workMinutes * 60);
    setRemainingSeconds(workMinutes * 60);
    setIsRunning(false);
    setIsPaused(false);
  }, [workMinutes, breakMinutes, rounds, currentRound, selectedBook, saveSessionStats, updateStreak, addRewards, playAlarm]);

  const advanceFromBreak = useCallback(() => {
    playAlarm();
    setPhase('study');
    setCurrentRound(currentRound + 1);
    setTotalSeconds(workMinutes * 60);
    setRemainingSeconds(workMinutes * 60);
    setIsRunning(false);
    setIsPaused(false);
  }, [workMinutes, currentRound, playAlarm]);

  completionRef.current = () => {
    const s = stateRef.current;
    if (s.phase === 'study') advanceFromStudy(true);
    else advanceFromBreak();
  };

  /* ============================================================
     STABLE TICK (1s interval — real time, phase-aware)
  ============================================================ */
  useEffect(() => {
    const id = setInterval(() => {
      const s = stateRef.current;
      if (!s.isRunning || s.isPaused) return;
      if (s.phase === 'study') {
        setRemainingSeconds((prev) => {
          if (prev <= 1) {
            setIsRunning(false);
            setTimeout(() => completionRef.current?.(), 0);
            return 0;
          }
          return prev - 1;
        });
      } else {
        setBreakRemaining((prev) => {
          if (prev <= 1) {
            setIsRunning(false);
            setTimeout(() => completionRef.current?.(), 0);
            return 0;
          }
          return prev - 1;
        });
      }
    }, 1000);
    return () => clearInterval(id);
  }, []);

  /* ============================================================
     CONTROLS
  ============================================================ */
  const startTimer = useCallback(() => {
    if (isRunning && !isPaused) return;
    if (isPaused) {
      setIsPaused(false);
      return;
    }
    if (phase === 'study' && remainingSeconds <= 0) {
      setRemainingSeconds(totalSeconds || workMinutes * 60);
    }
    if (phase === 'break' && breakRemaining <= 0) {
      setBreakRemaining(breakTotal || breakMinutes * 60);
    }
    setIsRunning(true);
    setIsPaused(false);
  }, [isRunning, isPaused, phase, remainingSeconds, breakRemaining, totalSeconds, breakTotal, workMinutes, breakMinutes]);

  const pauseTimer = useCallback(() => {
    if (!isRunning || isPaused) return;
    setIsPaused(true);
  }, [isRunning, isPaused]);

  const resetTimer = useCallback(() => {
    setIsRunning(false);
    setIsPaused(false);
    setPhase('study');
    setCurrentRound(1);
    setTotalSeconds(workMinutes * 60);
    setRemainingSeconds(workMinutes * 60);
    setBreakRemaining(0);
    setConfettiKey(0);
  }, [workMinutes]);

  const handlePlayToggle = useCallback(() => {
    if (phase === 'break') {
      if (isRunning && !isPaused) pauseTimer();
      else startTimer();
      return;
    }
    if (isRunning && !isPaused) { pauseTimer(); return; }
    if (isPaused) { startTimer(); return; }
    if (currentRound === 1 && remainingSeconds === totalSeconds && !isRunning && !isPaused) {
      setIsSessionOpen(true);
      return;
    }
    startTimer();
  }, [phase, isRunning, isPaused, remainingSeconds, totalSeconds, currentRound, pauseTimer, startTimer]);

  const skipCurrent = useCallback(() => {
    if (phase === 'study') {
      setIsRunning(false);
      setIsPaused(false);
      advanceFromStudy(false);
    } else {
      setIsRunning(false);
      setIsPaused(false);
      advanceFromBreak();
    }
  }, [phase, advanceFromStudy, advanceFromBreak]);

  /* ============================================================
     MODE SELECTION
  ============================================================ */
  const applyModeConfig = (key, name, w, b, r) => {
    if (isRunning || isPaused) {
      if (!window.confirm('تایمر در حال اجراست. تغییر حالت باعث ریست می‌شود. ادامه؟')) return;
    }
    setModeKey(key);
    setModeName(name);
    setWorkMinutes(w);
    setBreakMinutes(b);
    setRounds(r);
    setIsRunning(false);
    setIsPaused(false);
    setPhase('study');
    setCurrentRound(1);
    setTotalSeconds(w * 60);
    setRemainingSeconds(w * 60);
    setBreakRemaining(0);
    setConfettiKey(0);
  };

  const selectPreset = (key) => {
    const p = PRESETS[key];
    if (!p) return;
    applyModeConfig(key, p.name, p.work, p.break, p.rounds);
  };

  const selectCustomMode = (m) => {
    applyModeConfig(`custom_${m.id}`, m.name, m.workMinutes, m.breakMinutes, m.rounds);
  };

  const deleteCustomMode = (id) => {
    if (!window.confirm('حذف این حالت؟')) return;
    setCustomModes((prev) => prev.filter((m) => m.id !== id));
    if (modeKey === `custom_${id}`) selectPreset('pomo');
  };

  /* ============================================================
     MODALS
  ============================================================ */
  const openSettings = () => {
    setFormWork(workMinutes);
    setFormBreak(breakMinutes);
    setFormRounds(rounds);
    setIsSettingsOpen(true);
  };

  const applySettings = () => {
    const w = Math.max(1, Math.min(720, parseInt(formWork, 10) || 25));
    const b = Math.max(0, Math.min(120, parseInt(formBreak, 10) || 0));
    const r = Math.max(0, Math.min(20, parseInt(formRounds, 10) || 4));
    applyModeConfig('custom', 'سفارشی', w, b, r);
    setIsSettingsOpen(false);
  };

  const openSession = () => setIsSessionOpen(true);

  const applySession = () => {
    setTotalSeconds(workMinutes * 60);
    setRemainingSeconds(workMinutes * 60);
    setCurrentRound(1);
    setPhase('study');
    setIsSessionOpen(false);
    startTimer();
  };

  const openAddMode = () => {
    setNewModeName('');
    setFormWork(workMinutes || 25);
    setFormBreak(breakMinutes || 5);
    setFormRounds(rounds || 4);
    setIsAddModeOpen(true);
  };

  const saveCustomMode = () => {
    const name = newModeName.trim();
    if (!name) { alert('لطفاً نام حالت را وارد کنید'); return; }
    const w = Math.max(1, Math.min(720, parseInt(formWork, 10) || 25));
    const b = Math.max(0, Math.min(120, parseInt(formBreak, 10) || 0));
    const r = Math.max(0, Math.min(20, parseInt(formRounds, 10) || 4));
    const newMode = {
      id: `m_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      name, workMinutes: w, breakMinutes: b, rounds: r,
      icon: '⭐', createdAt: new Date().toISOString(),
    };
    setCustomModes((prev) => [...prev, newMode]);
    setIsAddModeOpen(false);
    selectCustomMode(newMode);
  };

  /* ============================================================
     KEYBOARD
  ============================================================ */
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (e.code === 'Space') { e.preventDefault(); handlePlayToggle(); }
      else if (e.key === 'r' || e.key === 'R') resetTimer();
      else if (e.key === 't' || e.key === 'T') openSettings();
      else if (e.key === 'Escape') {
        setIsSettingsOpen(false);
        setIsSessionOpen(false);
        setIsAddModeOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handlePlayToggle, resetTimer]); // eslint-disable-line

  /* ============================================================
     DERIVED VIEW STATE
  ============================================================ */
  const isBreak = phase === 'break';
  const currentRemaining = isBreak ? breakRemaining : remainingSeconds;
  const currentTotal = isBreak ? breakTotal : totalSeconds;
  const progress = currentTotal > 0 ? Math.min(1, Math.max(0, currentRemaining / currentTotal)) : 0;

  const R = 138, CX = 150, CY = 150, CIRC = 2 * Math.PI * R;
  // Arc: progress shrinks from 1 → 0; stroke = progress * CIRC (dash shrinks as time passes)
  const dashOffset = CIRC * (1 - progress);
  // Dot position: start at 12 o'clock, move clockwise as time passes
  // At progress=1 → angle=0 (top). At progress=0 → angle=2π (back to top)
const dotAngle = (1 - progress) * 2 * Math.PI;
const dotX = CX + R * Math.cos(dotAngle);
const dotY = CY + R * Math.sin(dotAngle);

  const phaseLabel = isBreak
    ? (isRunning && !isPaused ? 'BREAK' : 'BREAK · PAUSED')
    : (isRunning && !isPaused ? 'FOCUS' : (isPaused ? 'PAUSED' : 'READY'));

  const tKey = todayKey();
  const todayMin = dailyProgress[tKey] || 0;
  const todayH = Math.floor(todayMin / 60);
  const todayM = todayMin % 60;

  const hasEverStudied = !!adaptiveGoal.lastActiveDate;
  const isFirstDayStudied = adaptiveGoal.lastActiveDate === tKey;
  const gapDays = adaptiveGoal.lastActiveDate ? daysBetween(adaptiveGoal.lastActiveDate, tKey) : 0;
  const isReturn = hasEverStudied && !isFirstDayStudied && gapDays > 1;
  const todayGoal = adaptiveGoal.goalDate === tKey ? (adaptiveGoal.todayGoal ?? null) : null;
  const goalReached = todayGoal != null && todayMin >= todayGoal;
  const isNewRecordToday = (adaptiveGoal.record || 0) > 0 && adaptiveGoal.recordDate === tKey;
  const todayPct = todayGoal != null && todayGoal > 0 ? Math.min(100, (todayMin / todayGoal) * 100) : 0;
  const remainMin = todayGoal != null ? Math.max(0, todayGoal - todayMin) : 0;

  const tIdx = todayIndex();
  const weekDayValues = (() => {
    const arr = [0, 0, 0, 0, 0, 0, 0];
    const now = new Date();
    for (let i = 0; i <= tIdx; i++) {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
      arr[tIdx - i] = dailyProgress[localYMD(d)] || 0;
    }
    return arr;
  })();
  const weekMax = Math.max(1, ...weekDayValues);
  const weekTotal = weekDayValues.reduce((a, b) => a + b, 0);

  const topBooks = Object.entries(bookStats)
    .filter(([, v]) => v > 0)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 4);
  const topBooksMax = topBooks.length ? Math.max(...topBooks.map(([, v]) => v)) : 1;
  const BOOK_COLORS = ['#7C74F0', '#38BDF8', '#F472B6', '#FBBF24'];

  const hasGrade = !!selectedGrade;
  const needsField = hasGrade && curriculumNeedsField(selectedGrade);
  const lacksField = needsField && !selectedField;

  /* ============================================================
     RENDER
  ============================================================ */
  return (
    <div className={`study-timer ${isBreak ? 'st-break-mode' : ''}`}>
      {confettiKey > 0 && <Confetti key={`cf-${confettiKey}`} count={110} duration={4000} />}
      {recordToastKey > 0 && <RecordToast key={`rt-${recordToastKey}`} value={adaptiveGoal.record} />}

      {/* HEADER */}
      <header className="st-header">
        <div className="st-brand">
          <div className="st-brand-mark">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="13" r="8" />
              <path d="M12 9v4l2 2M9 2h6" />
            </svg>
          </div>
          <span>اتاق مطالعه</span>
        </div>
        <div className="st-header-stats">
          <div className="st-stat-chip">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />
            </svg>
            <strong>{fa(streak)}</strong> روز پیوسته
          </div>
          <button
            className="st-btn-icon"
            onClick={() => navigate(-1)}
            title="بازگشت"
            aria-label="بازگشت"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
        </div>
      </header>

      {/* MAIN */}
      <main className="st-main">
        <section className="st-hero">
          <div className="st-mode-pills">
            {Object.entries(PRESETS).map(([key, p]) => {
              const active = modeKey === key;
              return (
                <button
                  key={key}
                  className={`st-mode-pill ${active ? 'st-active' : ''}`}
                  onClick={() => selectPreset(key)}
                >
                  {p.name}
                </button>
              );
            })}
            {customModes.map((m) => {
              const active = modeKey === `custom_${m.id}`;
              return (
                <button
                  key={m.id}
                  className={`st-mode-pill st-custom ${active ? 'st-active' : ''}`}
                  onClick={() => selectCustomMode(m)}
                >
                  ⭐ {m.name}
                  <span
                    className="st-mode-delete"
                    onClick={(e) => { e.stopPropagation(); deleteCustomMode(m.id); }}
                    title="حذف حالت"
                  >×</span>
                </button>
              );
            })}
            <button className="st-mode-pill st-add-mode" onClick={openAddMode}>+ حالت جدید</button>
          </div>

          <div
            className="st-timer-wrap"
            onClick={handlePlayToggle}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handlePlayToggle(); } }}
          >
            <svg className="st-timer-ring" viewBox="0 0 300 300">
              <defs>
                <linearGradient id="stRingGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#7C74F0" />
                  <stop offset="50%" stopColor="#A78BFA" />
                  <stop offset="100%" stopColor="#C4B5FD" />
                </linearGradient>
              </defs>
              <circle className="st-ring-track" cx={CX} cy={CY} r={R} />
              <circle
                className="st-ring-progress"
                cx={CX} cy={CY} r={R}
                strokeDasharray={CIRC}
                strokeDashoffset={-dashOffset}
              />
              <g
                className="st-ring-dot-group"
                style={{ transform: `translate(${dotX}px, ${dotY}px)` }}
              >
                <circle className="st-ring-dot-halo" cx="0" cy="0" r="14" />
                <circle className="st-ring-dot" cx="0" cy="0" r="7" />
              </g>
            </svg>
            <div className="st-timer-inner">
              <div className="st-timer-phase">{phaseLabel}</div>
              <div className="st-timer-display">{formatTime(currentRemaining)}</div>
            </div>
          </div>

          <button className="st-session" onClick={openSession}>
            <span className="st-session-book">{selectedBook || 'انتخاب کتاب'}</span>
            {selectedChapter && (
              <>
                <span className="st-session-sep" />
                <span>{selectedChapter}</span>
              </>
            )}
            <span className="st-session-sep" />
            <span className="st-session-mode">{modeName}</span>
            <span className="st-session-edit">✎</span>
          </button>

          <div className="st-controls">
            <button className="st-btn-icon" onClick={resetTimer} title="ریست">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                <path d="M3 3v5h5" />
              </svg>
            </button>
            <button className="st-btn-play" onClick={handlePlayToggle}>
              {isRunning && !isPaused ? (
                <>
                  <svg viewBox="0 0 24 24"><path d="M6 4h4v16H6zM14 4h4v16h-4z" /></svg>
                  توقف
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                  {isPaused ? 'ادامه' : 'شروع'}
                </>
              )}
            </button>
            <button className="st-btn-icon" onClick={skipCurrent} title="رد کردن">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 4l10 8-10 8zM19 5v14" />
              </svg>
            </button>
          </div>

          <div className="st-rounds">
            {rounds === 0 ? (
              <span className="st-rounds-label">حالت بی‌نهایت ∞</span>
            ) : (
              <>
                {Array.from({ length: rounds }, (_, i) => {
                  const n = i + 1;
                  const cls = n < currentRound ? 'st-done' : n === currentRound ? 'st-current' : '';
                  return <span key={n} className={`st-round-dot ${cls}`} />;
                })}
                <span className="st-rounds-label">{fa(currentRound)}/{fa(rounds)}</span>
              </>
            )}
          </div>
        </section>

        <aside className="st-sidebar">
          <div className="st-card">
            <div className="st-card-label">امروز</div>

            {!hasEverStudied ? (
              <>
                <div className="st-today-value">
                  <span>{fa(todayH)}:{fa(pad(todayM))}</span>
                </div>
                <div className="st-goal-first">
                  🌟 امروز آزاد بمون<br />
                  هرچی می‌خوای مطالعه کن — از فردا با هم هدف می‌سازیم
                </div>
              </>
            ) : isFirstDayStudied && todayGoal == null ? (
              <>
                <div className="st-today-value">
                  <span>{fa(todayH)}:{fa(pad(todayM))}</span>
                </div>
                <div className="st-goal-first">
                  ✨ فوق‌العاده شروع کردی!<br />
                  از فردا قدم‌به‌قدم با هم جلو می‌ریم
                </div>
              </>
            ) : todayGoal != null ? (
              <>
                <div className="st-today-value">
                  <span>{fa(todayH)}:{fa(pad(todayM))}</span>
                  <span className="st-unit">از {fa(formatHM(todayGoal))}</span>
                </div>
                <div className="st-progress-track">
                  <div className="st-progress-fill" style={{ width: `${todayPct}%` }} />
                </div>
                <div className="st-progress-info">
                  <span>{fa(Math.round(todayPct))}٪</span>
                  {goalReached ? (
                    <strong className="st-goal-hit">✓ هدف امروز گرفتی</strong>
                  ) : (
                    <strong>{fa(formatHM(remainMin))} باقی</strong>
                  )}
                </div>
                {isReturn && !goalReached && (
                  <div className="st-goal-note">
                    👋 خوش برگشتی — از {fa(formatHM(todayGoal))} شروع می‌کنیم، نه از رکوردت
                  </div>
                )}
                {isNewRecordToday ? (
                  <div className="st-record-badge">🏆 رکورد جدید!</div>
                ) : (adaptiveGoal.record || 0) > 0 ? (
                  <div className="st-record-row">
                    <span>رکورد شما</span>
                    <span className="st-record-val">{fa(formatHM(adaptiveGoal.record))}</span>
                  </div>
                ) : null}
              </>
            ) : null}
          </div>

          <div className="st-card">
            <div className="st-week-header">
              <div className="st-card-label" style={{ margin: 0 }}>این هفته</div>
              <span className="st-week-total">{fa(weekTotal)} دقیقه</span>
            </div>
            <div className="st-week-chart">
            {WEEK_DAYS.map((d, i) => {
              const v = weekDayValues[i] || 0;
              const pct = (v / weekMax) * 100;
                return (
                  <div key={i} className={`st-week-col ${i === tIdx ? 'st-today' : ''}`}>
                    <div className="st-week-bar-wrap">
                      <div className="st-week-bar" style={{ height: `${Math.max(4, pct)}%` }} />
                    </div>
                    <span className="st-week-day">{d}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="st-card">
            <div className="st-card-label">کتاب‌ها</div>
            <div className="st-book-list">
              {topBooks.length === 0 ? (
                <div className="st-empty-text">هنوز جلسه‌ای ثبت نشده</div>
              ) : (
                topBooks.map(([name, val], i) => {
                  const pct = (val / topBooksMax) * 100;
                  const color = BOOK_COLORS[i % BOOK_COLORS.length];
                  const h = Math.floor(val / 60), m = val % 60;
                  const timeStr = h > 0 ? `${fa(h)}س ${fa(m)}د` : `${fa(m)}د`;
                  return (
                    <div key={name} className="st-book-row">
                      <div className="st-book-meta">
                        <span className="st-book-name">{name}</span>
                        <span className="st-book-time">{timeStr}</span>
                      </div>
                      <div className="st-book-bar-track">
                        <div className="st-book-bar-fill" style={{ width: `${pct}%`, background: color }} />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </aside>
      </main>

      <StudyLeaderboard
        currentUserId={userId}
        currentUserIsGuest={!user?.id || String(userId).startsWith('guest_')}
        refreshTrigger={refreshTrigger}
      />

      {/* BREAK OVERLAY */}
      {isBreak && (
        <div className="st-break-overlay">
          <div className="st-breath-circle">
            <div className="st-breath-time">{formatTime(breakRemaining)}</div>
          </div>
          <div className="st-break-msg">بلند شو، آب بخور<br />به اطراف نگاه کن</div>
          <button className="st-btn-end-break" onClick={skipCurrent}>پایان استراحت</button>
        </div>
      )}

      {/* SETTINGS MODAL */}
      {isSettingsOpen && (
        <div className="st-modal-backdrop" onClick={() => setIsSettingsOpen(false)}>
          <div className="st-modal" onClick={(e) => e.stopPropagation()}>
            <div className="st-modal-header">
              <div className="st-modal-title">تنظیمات تایمر</div>
              <button className="st-modal-close" onClick={() => setIsSettingsOpen(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="st-field">
              <label className="st-field-label">مدت مطالعه (دقیقه)</label>
              <input
                className="st-field-input"
                type="number"
                value={formWork}
                onChange={(e) => setFormWork(e.target.value)}
                onFocus={(e) => e.target.select()}
                min="1" max="720"
              />
            </div>
            <div className="st-field">
              <label className="st-field-label">مدت استراحت (دقیقه)</label>
              <input
                className="st-field-input"
                type="number"
                value={formBreak}
                onChange={(e) => setFormBreak(e.target.value)}
                onFocus={(e) => e.target.select()}
                min="0" max="120"
              />
            </div>
            <div className="st-field">
              <label className="st-field-label">تعداد دور (۰ = بی‌نهایت)</label>
              <input
                className="st-field-input"
                type="number"
                value={formRounds}
                onChange={(e) => setFormRounds(e.target.value)}
                onFocus={(e) => e.target.select()}
                min="0" max="20"
              />
            </div>
            <div className="st-modal-actions">
              <button className="st-btn-modal st-secondary" onClick={() => setIsSettingsOpen(false)}>انصراف</button>
              <button className="st-btn-modal st-primary" onClick={applySettings}>ذخیره</button>
            </div>
          </div>
        </div>
      )}

      {/* SESSION MODAL */}
      {isSessionOpen && (
        <div className="st-modal-backdrop" onClick={() => setIsSessionOpen(false)}>
          <div className="st-modal" onClick={(e) => e.stopPropagation()}>
            <div className="st-modal-header">
              <div className="st-modal-title">جلسه مطالعه</div>
              <button className="st-modal-close" onClick={() => setIsSessionOpen(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            {!hasGrade ? (
              <div className="st-session-message">
                🎓 برای انتخاب کتاب، ابتدا <strong>پایه تحصیلی</strong> خود را در تنظیمات پروفایل وارد کنید
              </div>
            ) : lacksField ? (
              <div className="st-session-message">
                🎯 برای انتخاب کتاب، ابتدا <strong>رشته تحصیلی</strong> خود را در تنظیمات پروفایل وارد کنید
              </div>
            ) : (
              <>
                <div className="st-field">
                  <label className="st-field-label">کتاب درسی (اختیاری)</label>
                  <select
                    className="st-field-input"
                    value={selectedBook}
                    onChange={(e) => setSelectedBook(e.target.value)}
                  >
                    <option value="">— بدون کتاب —</option>
                    {availableBooks.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>
                {availableChapters.length > 0 && (
                  <div className="st-field">
                    <label className="st-field-label">فصل / درس (اختیاری)</label>
                    <select
                      className="st-field-input"
                      value={selectedChapter}
                      onChange={(e) => setSelectedChapter(e.target.value)}
                    >
                      <option value="">— بدون فصل —</option>
                      {availableChapters.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                )}
              </>
            )}

            <div className="st-modal-actions">
              <button className="st-btn-modal st-secondary" onClick={() => setIsSessionOpen(false)}>انصراف</button>
              <button
                className="st-btn-modal st-primary"
                onClick={applySession}
                disabled={!hasGrade || lacksField}
                style={(lacksField || !hasGrade) ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
              >
                شروع مطالعه
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADD MODE MODAL */}
      {isAddModeOpen && (
        <div className="st-modal-backdrop" onClick={() => setIsAddModeOpen(false)}>
          <div className="st-modal" onClick={(e) => e.stopPropagation()}>
            <div className="st-modal-header">
              <div className="st-modal-title">حالت جدید</div>
              <button className="st-modal-close" onClick={() => setIsAddModeOpen(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="st-field">
              <label className="st-field-label">نام حالت</label>
              <input
                className="st-field-input"
                type="text"
                value={newModeName}
                onChange={(e) => setNewModeName(e.target.value)}
                placeholder="مثلاً مطالعه شبانه"
                maxLength={20}
              />
            </div>
            <div className="st-field">
              <label className="st-field-label">مدت مطالعه (دقیقه)</label>
              <input
                className="st-field-input"
                type="number"
                value={formWork}
                onChange={(e) => setFormWork(e.target.value)}
                onFocus={(e) => e.target.select()}
                min="1" max="720"
              />
            </div>
            <div className="st-field">
              <label className="st-field-label">مدت استراحت (دقیقه)</label>
              <input
                className="st-field-input"
                type="number"
                value={formBreak}
                onChange={(e) => setFormBreak(e.target.value)}
                onFocus={(e) => e.target.select()}
                min="0" max="120"
              />
            </div>
            <div className="st-field">
              <label className="st-field-label">تعداد دور (۰ = بی‌نهایت)</label>
              <input
                className="st-field-input"
                type="number"
                value={formRounds}
                onChange={(e) => setFormRounds(e.target.value)}
                onFocus={(e) => e.target.select()}
                min="0" max="20"
              />
            </div>
            <div className="st-modal-actions">
              <button className="st-btn-modal st-secondary" onClick={() => setIsAddModeOpen(false)}>انصراف</button>
              <button className="st-btn-modal st-primary" onClick={saveCustomMode}>ذخیره</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudyTimer;